import { useEffect, useMemo, useState } from "react";
import type { CSSProperties, JSX } from "react";
import type { AuthState } from "../shared/auth";
import { fetchAuthState, logout } from "./api";

type Theme = "dark" | "light";

export function App(): JSX.Element {
  const [authState, setAuthState] = useState<AuthState | undefined>(undefined);
  const [theme, setTheme] = useState<Theme>(() => readInitialTheme());

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  useEffect(() => {
    let active = true;

    fetchAuthState()
      .then((state) => {
        if (active) {
          setAuthState(state);
        }
      })
      .catch(() => {
        if (active) {
          setAuthState({ status: "unauthenticated", environment: "production" });
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const statusText = useMemo(() => {
    if (authState === undefined) {
      return "Checking session";
    }

    if (authState.status === "authenticated") {
      return `Signed in as ${authState.user.email}`;
    }

    return "Sign in required";
  }, [authState]);

  return (
    <main className="app-shell" style={{ "--mod-h": 160 } as CSSProperties}>
      <header className="top-bar">
        <div>
          <p className="eyebrow">Cairn</p>
          <h1>
            Personal <em>basecamp</em>
          </h1>
        </div>
        <button
          className="icon-button"
          type="button"
          aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
          title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
          onClick={() => {
            setTheme(theme === "dark" ? "light" : "dark");
          }}
        >
          {theme === "dark" ? <SunIcon /> : <MoonIcon />}
        </button>
      </header>

      <section className="status-card privacy-household" aria-live="polite">
        <div>
          <span className="status-dot" />
          <p className="card-label">Session</p>
          <h2>{statusText}</h2>
        </div>
        <EnvironmentBadge value={authState?.environment ?? "local"} />
      </section>

      {authState?.status === "unauthenticated" ? (
        <a className="primary-action" href="/auth/login">
          Sign in with Google
        </a>
      ) : null}

      {authState?.status === "authenticated" && authState.environment !== "local" ? (
        <button
          className="secondary-action"
          type="button"
          onClick={() => {
            void logout().then(() => {
              window.location.reload();
            });
          }}
        >
          Sign out
        </button>
      ) : null}

      <section className="module-grid" aria-label="Design system preview">
        <PreviewCard title="Private" description="Dusty violet privacy marker." className="privacy-private" />
        <PreviewCard title="Shared" description="Sage marker for collaborative data." className="privacy-shared" />
        <PreviewCard title="Household" description="Amber marker for home context." className="privacy-household" />
      </section>
    </main>
  );
}

function EnvironmentBadge({ value }: { value: string }): JSX.Element {
  return <span className="environment-badge">{value}</span>;
}

function PreviewCard({ title, description, className }: { title: string; description: string; className: string }): JSX.Element {
  return (
    <article className={`preview-card ${className}`}>
      <p className="card-label">{title}</p>
      <p>{description}</p>
    </article>
  );
}

function SunIcon(): JSX.Element {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4 12H2M22 12h-2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4" />
    </svg>
  );
}

function MoonIcon(): JSX.Element {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 14.6A7.7 7.7 0 0 1 9.4 4a8.7 8.7 0 1 0 10.6 10.6Z" />
    </svg>
  );
}

function readInitialTheme(): Theme {
  return document.documentElement.dataset.theme === "light" ? "light" : "dark";
}
